import React from 'react';
import { Building2, ArrowRight } from 'lucide-react';

export interface CompanyCandidate {
  name: string;
  domain: string;
  logoUrl?: string | null;
}

export interface CompanyCandidatesListProps {
  candidates: CompanyCandidate[] | null;
  onSelectCandidate: (domain: string) => void;
  isLoading?: boolean;
  headerText?: string;
  emptyTitle?: string;
  emptySubtitle?: string;
  className?: string;
}

export const CompanyCandidatesList: React.FC<CompanyCandidatesListProps> = ({
  candidates,
  onSelectCandidate,
  isLoading = false,
  headerText = 'Select your company to load its logo:',
  emptyTitle = 'No matching companies found',
  emptySubtitle = "Try entering your website URL directly (e.g. yourcompany.com) instead.",
  className = '',
}) => {
  if (!candidates) return null;

  if (candidates.length === 0) {
    return (
      <div className={`rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-center ${className}`}>
        <p className="text-xs font-semibold text-slate-700">{emptyTitle}</p>
        <p className="text-[11px] text-slate-500 mt-1">{emptySubtitle}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-2 animate-in fade-in slide-in-from-top-2 duration-300 ${className}`}>
      {headerText && (
        <p className="text-xs font-semibold text-slate-600 px-1">{headerText}</p>
      )}
      <div className="rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100 max-h-56 overflow-y-auto">
        {candidates.map((cand) => (
          <button
            key={cand.domain}
            type="button"
            onClick={() => onSelectCandidate(cand.domain)}
            disabled={isLoading}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-[#A655F8]/5 focus:bg-[#A655F8]/5 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-9 h-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden shrink-0 shadow-2xs">
              {cand.logoUrl ? (
                <img
                  src={cand.logoUrl}
                  alt={cand.name}
                  className="w-full h-full object-contain p-1"
                />
              ) : (
                <Building2 className="w-4 h-4 text-slate-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-900 truncate">
                {cand.name}
              </p>
              <p className="text-[11px] text-slate-500 truncate">
                {cand.domain}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
};
